import type { SheetState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { isRoughlyOnLand } from './landMask.ts'
import { effectiveMetadata, parseCoordinate } from './sheetImport.ts'
import type { ColumnTarget } from './sheetMapping.ts'
import { columnTargets, retarget } from './sheetMapping.ts'

type SheetColumns = Pick<SheetState, 'headers' | 'rows' | 'mapping' | 'extraColumns'>

export type CoordinateSwapHint = {
  latColumn: string
  lonColumn: string
  /** Points that are only plausible with the columns swapped, of all readable points. */
  swapOnly: number
  points: number
}

const MIN_POINTS = 3

const plausible = (lat: number, lon: number) => Math.abs(lat) <= 90 && isRoughlyOnLand(lat, lon)

/**
 * Separate latitude/longitude columns that look swapped: most points are at sea or impossible (|lat| > 90)
 * as written but on land swapped, and almost none are plausible only as written. Points plausible both
 * ways decide nothing, so a sheet of them never warns; with fewer than three points only impossible
 * latitudes count as evidence.
 */
export const coordinateSwapHint = (sheet: SheetColumns): CoordinateSwapHint | undefined => {
  const columns = effectiveMetadata(sheet.mapping, sheet.extraColumns)
  const { coordinatesLat: latColumn, coordinatesLon: lonColumn } = columns
  if (!latColumn || !lonColumn || columns.coordinatesMerged) {
    return undefined
  }
  const points = sheet.rows.flatMap((row) => {
    const lat = parseCoordinate(row[latColumn]?.replace(',', '.'), 180)
    const lon = parseCoordinate(row[lonColumn]?.replace(',', '.'), 180)
    return lat === undefined || lon === undefined || (lat === 0 && lon === 0) ? [] : [{ lat, lon }]
  })
  const asWritten = points.map(({ lat, lon }) => plausible(lat, lon))
  const swapped = points.map(({ lat, lon }) => plausible(lon, lat))
  const swapOnly = points.filter((_, i) => swapped[i] && !asWritten[i]).length
  const writtenOnly = points.filter((_, i) => asWritten[i] && !swapped[i]).length
  const impossible = points.filter(({ lat, lon }) => Math.abs(lat) > 90 && Math.abs(lon) <= 90).length
  const enough = points.length >= MIN_POINTS || (points.length > 0 && impossible === points.length)
  if (!enough || swapOnly * 2 <= points.length || writtenOnly * 10 > points.length) {
    return undefined
  }
  return { latColumn, lonColumn, swapOnly, points: points.length }
}

/** The sheet's column targets with the latitude and longitude columns exchanged. */
export const swappedCoordinateTargets = (
  sheet: Pick<SheetState, 'headers' | 'mapping' | 'extraColumns'>,
  hint: Pick<CoordinateSwapHint, 'latColumn' | 'lonColumn'>
): Record<string, ColumnTarget> =>
  retarget(retarget(columnTargets(sheet), hint.latColumn, 'coordinatesLon'), hint.lonColumn, 'coordinatesLat')

export const describeCoordinateSwap = (hint: CoordinateSwapHint): string =>
  `Latitude and longitude look swapped: ${hint.swapOnly} of ${hint.points} points are at sea or out of range as written, and on land with "${hint.latColumn}" and "${hint.lonColumn}" exchanged.`
